import React, { MouseEvent, useState, Dispatch, useEffect, ChangeEvent, useRef, useMemo } from 'react';
import Modal from 'react-modal';
import Select, { components } from 'react-select'
import { startGame, leaveGame, postOrganization } from '../../features/player/gameSlice'
import { fetchOrganizations, createOrganization, selectOrganizationsSlice } from '../../features/organizations/organizationSlice'
import { fetchGameStatus, selectGamesStatus } from '../../features/player/gameStatusSlice'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { IGame, IPlayer, IGameStatus, ISelectedOption, IOrganization } from '../../interfaces';
import { GameStatus, SelectedOption } from '../../Classes1';
import Participant from './Participant';
import Host from './Host';

interface GameLobbyProps {
    showGameLobby: string;
    toggleGameLobby: Dispatch<any>;
    setShowGame: Dispatch<any>;
    gameScenario: any;
    setIsHosting: Dispatch<boolean>;
    isHosting: boolean;
    currentGame: IGame;
    setShowGameLobby: Dispatch<any>;
    calcGameLobbyHeight: Dispatch<any>;
    intervalHandler: (command: string, gameId: string, inGame: boolean) => void;
    gamePassword: string;
    rejectModalIsOpen: boolean;
    gameStartedModalIsOpen: boolean;
}

interface AccordionItem {
    type: string, 
    marginTop: number, 
    orgId: string,
    orgName: string,
    count: number,
    readyCount: number
}

interface IPlayerItem {
    email: string;
    orgId: string;
    orgName: string;
    selected: boolean;
}

class PlayerItem {
    email: string = '';
    orgId: string = '';
    orgName: string = '';
    selected: boolean = false;
}

Modal.setAppElement('#root');

const GameLobby: React.FC<GameLobbyProps> = ({ showGameLobby, toggleGameLobby, gameScenario, setShowGame, isHosting, setIsHosting, currentGame, setShowGameLobby, calcGameLobbyHeight, intervalHandler, gamePassword, rejectModalIsOpen, gameStartedModalIsOpen }) => {

    const [errorModalIsOpen, setErrorModalIsOpen] = useState<boolean>(false);
    const [errorText, setErrorText] = useState<string>('');
    const [showJoinSection, setShowJoinSection] = useState<string>('show-inline');
    const [showParticipantsSection, setShowParticipantsSection] = useState<string>('');
    const [accordionArray, setAccordionArray] = useState<AccordionItem[]>([]);
    const [leaveGameModalIsOpen, setLeaveGameModalIsOpen] = useState<boolean>(false);
    const [manageOrgsModalIsOpen, setManageOrgsModalIsOpen] = useState<boolean>(false);
    const [selectedOption, setSelectedOption] = useState<ISelectedOption | null>(new SelectedOption({}))
    const [newOrg, setNewOrg] = useState<boolean>(false);
    const [newOrgVal, setNewOrgVal] = useState<string>('');
    const [orgOptions, setOrgOptions] = useState<ISelectedOption[] | null>([]);
    const [currentPlayers, setCurrentPlayers] = useState<PlayerItem[]>([]);
    const [orgLoading, setOrgLoading] = useState<string>('');
    const [noOrgs, setNoOrgs] = useState<IPlayer[]>([]);
    const [startLoading, setStartLoading] = useState<boolean>(false);
    const [leaveGameLoading, setLeaveGameLoading] = useState<boolean>(false)
    
    const dispatch = useAppDispatch();

    const user = useAppSelector((state) => state.auth.userId)

    useEffect(() => {
        async function getOrgs() {
            await dispatch(fetchOrganizations())
            buildOrgOptions()
        }
        getOrgs()
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    
    let currentGameStatus: IGameStatus = useAppSelector(selectGamesStatus)[0];
    if (currentGameStatus === undefined) {
        currentGameStatus = new GameStatus();
    }

    let organizations: IOrganization[] = useAppSelector(selectOrganizationsSlice);
    if (organizations === undefined) {
        organizations = [];
    }

    const gameStatusRef = useRef<any>()
    useEffect(() => {
        gameStatusRef.current = currentGameStatus
    });

    const [sortedRoles, setSortedRoles] = useState(currentGameStatus.roles);

    useEffect(() => {
        const scenRoles : string[] = gameScenario.roles;
        const toSort = currentGameStatus.roles.slice();
        toSort.sort(
            (role1, role2) => scenRoles.indexOf(role1) - scenRoles.indexOf(role2)
        );
        setSortedRoles(toSort);
    }, [currentGameStatus.roles, gameScenario.roles])

    useEffect(() => {
        setIsHosting(currentGameStatus.host === user)
    }, [user, currentGameStatus.host, setIsHosting])

    const organizationsRef = useRef<any>()
    useEffect(() => {
        organizationsRef.current = organizations
    });

    const CustomDropdownIndicator = (props: any) => {
        return (
          <components.DropdownIndicator {...props}>
            <img src="img/chevron-down-grey.svg" className="chevron-down-grey" alt="arrow" />
          </components.DropdownIndicator>
        );
    };

    const CustomClearIndicator = (props: any) => {
        return (
          <components.ClearIndicator {...props}>
            <img src="/img/light-grey-x.svg" className="custom-clear-indicator" alt="x"></img>
          </components.ClearIndicator>
        );
    };

    const NoOptionsMessage = (props: any) => {
        return (
          <components.NoOptionsMessage {...props}>
            <span className="no-options-message">No Results</span> 
          </components.NoOptionsMessage>
        );
      };

    const customStyles = {
        content: {
            background: '#323544',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            padding: '0',
            border: 'none',
            width: '480px',
            transform: 'translate(-50%, -50%)',
        }
    };
    
    const customLargeStyles = {
        content: {
            background: '#323544',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            padding: '0',
            border: 'none',
            width: '640px',
            overflow: 'inherit',
            transform: 'translate(-50%, -50%)',
        }
    };
    
    const dropdownStyles = {
        option: (provided: any, { isFocused, isSelected } : { isFocused: any, isSelected: any }) => ({
            ...provided,
            backgroundColor: isSelected ? '#3F83FC' : '#191B22',
            '&:hover': { backgroundColor: isFocused ? '#3F83FC' : '#191B22' },
            fontSize: 14,
            color: "#F4F4F4"
        }),
        singleValue: (provided: any, state: any) => ({
            ...provided,
            padding: 0,
            color: '#F4F4F4',
            backgroundColor: "#191B22",
            width: 376,
            fontSize: 14,
            marginLeft: 8
        }),
        container:  (provided: any, state: any) => ({
            ...provided,
            border: "none",
            width: 376,
            height: 48
        }),
        control:  (provided: any, state: any) => ({
            ...provided,
            backgroundColor: "#191B22",
            color: "#F4F4F4",
            borderRadius: 0,
            border: "none",
            boxShadow: 0,
            width: 376,
            height: 48
        }),
        placeholder:  (provided: any, state: any) => ({
            ...provided,
            color: "#F4F4F4",
            fontSize: 14
        }),
        indicatorSeparator:  (provided: any, state: any) => ({
            ...provided,
            display: "none"
        }),
        noOptionsMessage:  (provided: any, state: any) => ({
            ...provided,
            backgroundColor: "#191B22",
            color: "#F4F4F4",
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 12,
            paddingRight: 12
        }),
        menu:  (provided: any, state: any) => ({
            ...provided,
            width: 376,
            color: '#F4F4F4',
            backgroundColor: "#191B22",
            top: 50
        }),
        input:  (provided: any, state: any) => ({
            ...provided,
            marginTop: 5,
            height: 37,
            color: '#F4F4F4'
        }),
        menuList:  (provided: any, state: any) => ({
            ...provided,
            color: '#161616',
            backgroundColor: "#F4F4F4",
            paddingTop: 0,
            paddingBottom: 0
        }),
        indicatorContainer:  (provided: any, state: any) => ({
            ...provided,
            color: '#F4F4F4'
        })
    }

    useEffect(() => {

        const tempOrgs: string[] = []
        const accArray: AccordionItem[] = []
        const allOrgs: AccordionItem[] = []

        if (organizations.length > 0) {
            for (let i = 0; i<organizations.length; i++) {
                let accItem: AccordionItem = { orgId: '', orgName: '', count: 0, readyCount: 0, type: 'open', marginTop: 0 }
            
                accItem.orgId = organizations[i].id;
                accItem.orgName = organizations[i].orgName;
            
                allOrgs.push(accItem)
            }
        }

        for (let i = 0; i<currentGameStatus.players.length; i++) {
            tempOrgs.push(currentGameStatus.players[i].orgId)
        }

        for (let j = 0; j<allOrgs.length; j++) {
            let orgCount: number = 0;
            let readyCount: number = 0;
            for (let i = 0; i<currentGameStatus.players.length; i++) {
                if (currentGameStatus.players[i].orgId === allOrgs[j].orgId) {
                    orgCount++;
                    if (currentGameStatus.players[i].role !== null) {
                        readyCount++
                    }
                }
            }
            allOrgs[j].count = orgCount
            allOrgs[j].readyCount = readyCount
        }

        let uniqueOrgs = tempOrgs.filter(function (v, i, self) {

            // It returns the index of the first
            // instance of each value
            return i === self.indexOf(v);
        });

        for (let i = 0; i<uniqueOrgs.length; i++) {
            accArray.push({type: 'open', marginTop: 0, orgId: uniqueOrgs[i], orgName: '', count: 0, readyCount: 0})
        }

        for (let i = 0; i<accordionArray.length; i++) {
            if (accordionArray[i].type === 'open') {
                for (let i = 0; i<allOrgs.length; i++) {
                    if (allOrgs[i].orgId === accordionArray[i].orgId) {
                        allOrgs[i].marginTop = accordionArray[i].marginTop
                    }
                }
            } else {
                allOrgs[i].type = 'closed'
            }
        }

        let noOrgsPlayer: IPlayer[] = [];
        
        for (let q = 0; q<currentGameStatus.players.length; q++) {
            let isInOrg: boolean = false;
            for (let r = 0; r<organizations.length; r++) {
                if (currentGameStatus.players[q].orgId === organizations[r].id) {
                    isInOrg = true;
                }
            }
            if (!isInOrg) {
                let noOrgPlayer = JSON.parse(JSON.stringify(currentGameStatus.players[q]))
                noOrgsPlayer.push(noOrgPlayer) 
            }
        }

        setNoOrgs(noOrgsPlayer)
        setAccordionArray(allOrgs)

      }, [currentGameStatus.players, organizations]) // eslint-disable-line react-hooks/exhaustive-deps
    

    useEffect(() => {
        intervalHandler("start", currentGame.gameId, true)
        if (currentGameStatus.started) {
            if (!currentGameStatus.isPrivate) {
                if (!rejectModalIsOpen) {
                    setShowGame('show-game')
                    setShowGameLobby('')
                } else {
                    setShowGame('')
                }
            } else {
                if (gamePassword === currentGameStatus.password && gamePassword !== '') {
                    setShowGame('show-game')
                    setShowGameLobby('')
                }
            }
        }
    }, [currentGameStatus.started, currentGame.gameId, rejectModalIsOpen, gameStartedModalIsOpen]) // eslint-disable-line react-hooks/exhaustive-deps

    const buildOrgOptions = async () => {
        await dispatch(fetchOrganizations())
        
        let orgOpts: ISelectedOption[] = [];
        for (let i = 0; i<organizationsRef.current.length; i++) {
            let orgOpt: ISelectedOption = { label: organizationsRef.current[i].orgName, value: organizationsRef.current[i].id }
            orgOpts.push(orgOpt)
        }
        
        setOrgOptions(orgOpts)
    };

    const openLeaveGameModal = () => {
        setLeaveGameModalIsOpen(true);
    }
    
    const closeLeaveGameModal = () => {
        setLeaveGameModalIsOpen(false);
    }

    /* Commented out until designs for teams/orgs from Adrian are delivered and integrated */
    /*const openManageOrgsModal = () => {
        setManageOrgsModalIsOpen(true);
    }*/
    
    const closeManageOrgsModal = () => {
        setManageOrgsModalIsOpen(false);
    }

    const openErrorModal = () => {
        setErrorModalIsOpen(true);
    }

    useEffect(() => {
        if (currentGameStatus.error) {
            if (/^Game with ID "\w+" does not exist.$/.test(currentGameStatus.error)) {
                setErrorText("Game Has Been Closed");
            }
            openErrorModal();
        }
    }, [currentGameStatus.error])


    const closeErrorModal = () => {
        setErrorModalIsOpen(false);
        if (errorText === 'Game Has Been Deleted') {
            window.location.href = "/player";
        }
    }

    const launchMainGame = async (ev: MouseEvent) => {
        if (startLoading) {
            return;
        }

        const target = ev.currentTarget as HTMLElement;
        if (target && target.classList.contains('disabled')) {
            return;
        }

        if (isHosting) {
            setStartLoading(true)
            const startGameResponse = await dispatch(startGame(currentGame.gameId))
            setStartLoading(false)
            if (startGameResponse.payload.status === 500) {
                setErrorText(startGameResponse.payload.data);
                openErrorModal();
            } else {
                await dispatch(fetchGameStatus(currentGame.gameId))
                setShowGame('show-game')
                setShowGameLobby('')
            }
        } else {
            await dispatch(fetchGameStatus(currentGame.gameId))
            setShowGame('show-game')
            setShowGameLobby('')
        }
    };

    const choosePanel = (ev: MouseEvent) => {
        const target = ev.currentTarget as HTMLElement;
        if (!target) { return }

        if (target.dataset.tabid === 'join') {
            setShowJoinSection('show-inline')
            setShowParticipantsSection('')
        }
        else if (target.dataset.tabid === 'participants') {
            setShowJoinSection('')
            setShowParticipantsSection('show-inline')
        }
    };

    const leaveMainGame = async (ev: MouseEvent) => {
        if (leaveGameLoading) { return; }

        setLeaveGameLoading(true)

        const leaveGameResponse = await dispatch(leaveGame(currentGame.gameId))
        if (leaveGameResponse.payload.status === 500) {
            setErrorText(leaveGameResponse.payload.data);
            openErrorModal();
        } else {
            await dispatch(fetchGameStatus(currentGame.gameId))
            closeLeaveGameModal()
            setLeaveGameLoading(false)
            window.location.href = "/player";
        }
    };

    const areAllReady = useMemo(() => {
        return currentGameStatus.players.every((player) => player.ready);
    }, [currentGameStatus.players])

    const clickOrgRow = (ev: MouseEvent) => {
        const target = ev.currentTarget as HTMLElement;
        if (!target) { return; }

        let rowHeight = 45;
        for (let i = 0; i<currentGameStatus.players.length; i++) {
            if (currentGameStatus.players[i].orgId === target.dataset.rowid) {
                rowHeight += 60;
            }
        }

        let accArray = JSON.parse(JSON.stringify(accordionArray))
        for (let i = 0; i<accordionArray.length; i++) {
            if (accordionArray[i].orgId === target.dataset.rowid) {
                if (accArray[i].type === 'open') {
                    accArray[i].type = 'close'
                    accArray[i].marginTop = -rowHeight
                } else {
                    accArray[i].type = 'open'
                    accArray[i].marginTop = 0
                }
            }
        }

        setAccordionArray(accArray);
    }

    /* Commented out until designs for teams/orgs from Adrian are delivered and integrated */
    /*const clickManageOrgs = () => {
        openManageOrgsModal()
    }*/

    const updateCurrentPlayers = (selectedOption: ISelectedOption) => {
        let newPlyrs: IPlayerItem[] = [];
        for (let i = 0; i<gameStatusRef.current.players.length; i++) {
            if (gameStatusRef.current.players[i].orgId === selectedOption.value) {
                let newPlyr: IPlayerItem = new PlayerItem();
                newPlyr.email = gameStatusRef.current.players[i].userID;
                newPlyr.orgId = gameStatusRef.current.players[i].orgId;
                newPlyr.orgName = selectedOption.label;
                newPlyr.selected = true;
                newPlyrs.push(newPlyr)
            } else {
                let newPlyr: IPlayerItem = new PlayerItem();
                newPlyr.email = gameStatusRef.current.players[i].userID;
                newPlyr.orgId = gameStatusRef.current.players[i].orgId;
                newPlyr.orgName = selectedOption.label;
                newPlyr.selected = false;
                newPlyrs.push(newPlyr)
            }
        }
        
        setCurrentPlayers(newPlyrs)
    }

    const handleOrgsChange = async (selectedOption: any) => {
        if (selectedOption === null) {
            setSelectedOption(new SelectedOption({}))
        } else {
            setSelectedOption(selectedOption)
            updateCurrentPlayers(selectedOption)
        }
    }

    const onChangeNewOrg = () => {
        setNewOrg(!newOrg)
    }

    const togglePlayer = async (ev: ChangeEvent<HTMLInputElement>) => {
        const target = ev.currentTarget as HTMLInputElement;
        if (!target) { return; }

        await dispatch(postOrganization({ gameId: currentGameStatus.gameId, orgId: target.dataset.orgid, playerId: target.dataset.email }))
        await dispatch(fetchGameStatus(currentGameStatus.gameId))

        updateCurrentPlayers({ label: target.dataset.orgname!, value: target.dataset.orgid! });
    }

    const onNewOrgInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const target = ev.currentTarget as HTMLInputElement;
        if (!target) { return; }

        setNewOrgVal(target.value)
    }

    const clickAddOrg = async (ev: MouseEvent) => {
        setOrgLoading('show');
        let createOrgResp: any = await dispatch(createOrganization(newOrgVal));
        
        await dispatch(fetchGameStatus(currentGameStatus.gameId))
        setSelectedOption({ label: createOrgResp.payload.orgName, value: createOrgResp.payload.id });
        setNewOrg(false)
        updateCurrentPlayers({ label: '', value: '' });

        setOrgLoading('');
    }

    const leaveOnUnload = useRef<any>();
    leaveOnUnload.current = () => {
        leaveMainGame({} as MouseEvent)
    }

    useEffect(() => {
        if (showGameLobby) {
            window.addEventListener('unload', leaveOnUnload.current);
        }
        else {
            window.removeEventListener('unload', leaveOnUnload.current);
        }
    }, [showGameLobby])

    return (
        <div className={`game-lobby ${showGameLobby}`}>
            <img className="scenario-x" src="img/grey-x.svg" onClick={openLeaveGameModal} alt="open scenario page" />
            <div className="launch-scenario-title">{currentGameStatus.name}</div>
            <div className="launch-scenario-subtitle">{gameScenario.description}</div>
            <div className="launch-scenario-label">
                <div className="launch-scenario-label-title">Host</div>
                <div className={`launch-scenario-label-text ${ currentGameStatus.host === '' ? 'no-host' : '' }`}>
                    <Host disabled={user !== currentGameStatus.host && currentGameStatus.host !== ''} currentGameId={currentGame.gameId} currentGameStatus={currentGameStatus} setIsHosting={setIsHosting} />
                </div>
            </div>
            { currentGameStatus.isPrivate ? 
            <div className="launch-scenario-label">
                <div className="launch-scenario-label-password">Password</div>
                <div className="launch-scenario-label-password-text">{ currentGame.password }</div>
            </div> : <></> }
            <div className="lobby-tab-section">
                <div className="lobby-tabs">
                    <div className={`lobby-tab-scenario ${showJoinSection}`} onClick={choosePanel}data-tabid="join">Join Scenario</div>
                    <div className={`lobby-tab-participant ${showParticipantsSection}`} onClick={choosePanel} data-tabid="participants">All Participants</div>
                    {/* Commented out until designs for teams/orgs from Adrian are delivered and integrated */}
                    {/* currentGameStatus.host === user ? <button className="blue-game-btn manage-orgs-btn" onClick={clickManageOrgs}>Manage Orgs</button> : <></> */}
                </div>
                <div className="lobby-sections">
                    <div className={`join-scenario-section ${showJoinSection}`}>
                        <div className="lobby-section-info">
                            <div className="join-scenario-title">Join Now</div>
                            <div className="join-scenario-instructions">Select your <span>Role</span> and click the <span>Join</span> button to join this scenario.</div>
                            <div className="join-scenario-roles">Available roles are:<br /> <span>{currentGame.roles.join(', ')}</span></div>
                        </div>
                        <div className="participants-table">
                            <div className="participants-table-header">
                                <div className="participants-header-item">Participant</div>
                                <div className="participants-header-item">Role</div>
                                <div className="participants-header-item">Status</div>
                            </div>
                            {currentGameStatus.players.filter((p) => p.userID === user).map((player: IPlayer) => {
                                return (
                                    <Participant disabled={false} player={player} currentGameId={currentGame.gameId} currentGameStatus={currentGameStatus} sortedRoles={sortedRoles} key={player.userID} />
                                )
                            })}
                            {!currentGameStatus.players.some((p) => p.userID === user) &&
                                (isHosting
                                ? <div>You have not yet been attached to this game.</div>
                                : <div>You have been removed from the game.</div>)
                            }
                        </div>
                    </div>
                    <div className={`all-participants-section ${showParticipantsSection}`}>
                        <div className="lobby-section-info">
                            <div className="join-scenario-title">Participants</div>
                            <div className="join-scenario-instructions">Once all participants have selected a role and clicked "Click here to ready", the game will begin.</div>
                            <div className="join-scenario-roles">Available roles are:<br /> <span>{currentGame.roles.join(', ')}</span></div>
                        </div>
                        <div className="participants-table">
                            <div className="participants-content">
                            <div className="participants-org-row no-orgs">No Organizations</div>
                                         { noOrgs.length > 0 ? 
                                                    <div className="participants-table-header">
                                                        <div className="participants-header-item">Participant</div>
                                                        <div className="participants-header-item">Role</div>
                                                        <div className="participants-header-item">Status</div>
                                                    </div>
                                                  : <></>}
                                                { noOrgs.length > 0 ? noOrgs.map((player: IPlayer) => {
                                                        return (
                                                            <Participant disabled={currentGameStatus.host !== user} player={player} currentGameId={currentGame.gameId} currentGameStatus={currentGameStatus} sortedRoles={sortedRoles} key={player.userID} />
                                                        )
                                                    })
                                                  : <></> }
                            {accordionArray.map((accItem, i) => {
                                if (accItem.count > 0) {
                                    return (
                                        <>
                                            <div className={`participants-org-row ${accItem.type}`} onClick={clickOrgRow} data-rowid={accItem.orgId}>
                                                <div className="org-row-title">{accItem.orgName}</div>
                                                <div className="org-row-count">{accItem.readyCount}/{accItem.count} Ready</div>
                                                <img className="org-row-chevron" src="img/light-grey-chevron.svg" alt="chevron"></img>
                                            </div>
                                            <div className={`org-row-item ${accItem.type}`} style={{ marginTop: accItem.marginTop + 'px', zIndex: '10' + (99 - i) }}>
                                                <div className="participants-table-header">
                                                    <div className="participants-header-item">Status</div>
                                                    <div className="participants-header-item">Participant</div>
                                                    <div className="participants-header-item">Role</div>
                                                </div>
                                                {isHosting
                                                ? currentGameStatus.players.length === 0
                                                    ? <div className="no-players">No Players Yet.</div>
                                                    : currentGameStatus.players.filter(
                                                        (plyr) => plyr.orgId === accItem.orgId
                                                    ).map((player: IPlayer) =>
                                                        <Participant disabled={(currentGameStatus.host !== user)} player={player} currentGameId={currentGame.gameId} currentGameStatus={currentGameStatus} sortedRoles={sortedRoles} key={player.userID} />
                                                    )
                                                : currentGameStatus.players.filter(
                                                    (plyr) => plyr.orgId === accItem.orgId
                                                ).map((player: IPlayer) =>
                                                    <Participant disabled={true} player={player} currentGameId={currentGame.gameId} currentGameStatus={currentGameStatus} sortedRoles={sortedRoles} key={player.userID} />
                                                )}
                                            </div>
                                    </> )
                                } else {
                                    return (<></>)
                                }
                            })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="game-lobby-footer">
            <div className="game-lobby-footer-item cancel" onClick={openLeaveGameModal}><span className="game-lobby-footer-item-text">Leave</span></div>
                
            {isHosting && areAllReady ?
            <button className={`game-lobby-footer-item action ${startLoading ? 'start-loading': ''}`} onClick={launchMainGame}>
                <span className="game-lobby-footer-item-text">Start</span>
                {startLoading ? <div className="save-load">
                    <div className="game-save-loader-30 game-save-loader"></div>
                </div> : '' }
            </button>
            :<button className="game-lobby-footer-item waiting disabled"><span className="game-lobby-footer-item-text loading-ellipsis">
                { currentGameStatus.started ? 'Started'
                : isHosting ? 'Waiting on players'
                : areAllReady ? 'Waiting on host'
                : 'Waiting on players'
                }
            </span></button>
            }
            </div>
            <Modal
                isOpen={errorModalIsOpen}
                onRequestClose={closeErrorModal}
                style={customStyles}
                contentLabel="Failed Host Modal"
            >
                <div className="enter-password">{errorText}</div>
                <button onClick={closeErrorModal} className="join-game delete-cancel-btn">Close</button>
            </Modal>

            <Modal
                isOpen={leaveGameModalIsOpen}
                onRequestClose={closeLeaveGameModal}
                style={customStyles}
                contentLabel="Kick Player Modal"
            >
                <div className="new-modal-header">Leave Game? <img src="/img/light-grey-x.svg" className="new-modal-padding-close" alt="x" onClick={closeLeaveGameModal}></img></div>
                
                <div className="new-modal-text">Are you sure you want to leave the game?</div>

                <button onClick={closeLeaveGameModal} className="new-cancel-btn"><span>No</span></button>
                <button onClick={leaveMainGame} className={`new-confirm-btn ${leaveGameLoading ? 'start-loading' : ''}`}>
                    {leaveGameLoading ? <div className="game-save-loader-25"></div> : <></> }
                    <span>Leave Game</span>
                </button>
            </Modal>
            <Modal
                isOpen={manageOrgsModalIsOpen}
                onRequestClose={closeManageOrgsModal}
                style={customLargeStyles}
                contentLabel="Manage Orgs Modal"
            >
                <div className="new-modal-header modal-large-header">Manage Organizations <img src="/img/light-grey-x.svg" className="new-modal-padding-close" alt="x" onClick={closeManageOrgsModal}></img></div>
                
                <div className="manage-orgs-content">
                    <div className={`manage-org-loading-screen ${orgLoading}`}>
                        <div className={`manage-org-loader scenario-loader-60`}></div>
                    </div>
                    <div className="org-list-label">Organizations</div>
                    
                    { newOrg ? <div className="add-org-content"><input type="text" className="new-org-input" value={newOrgVal} onChange={onNewOrgInputChange} /><button className="blue-game-btn add-org-btn" onClick={clickAddOrg}>Add</button></div> : <Select
                        value={{ label: selectedOption!.label, value: selectedOption!.value }}
                        onChange={handleOrgsChange}
                        components={{ DropdownIndicator: CustomDropdownIndicator, ClearIndicator: CustomClearIndicator, NoOptionsMessage }}
                        styles={dropdownStyles}
                        options={orgOptions!}
                        isClearable
                        isSearchable={true}
                        className="manage-org-select"
                    /> }
                    <label htmlFor="org-new-checkbox" className="org-new-label">New</label>
                    <div className="checkbox-wrapper-14 org-new-checkbox">
                        <input className="tgl tgl-ios" id="cb2-100" type="checkbox" value={newOrg.toString()} checked={newOrg} onChange={onChangeNewOrg} />
                        <label className="tgl-btn" htmlFor="cb2-100"></label>
                    </div>

                    <div className="org-user-list">
                        <div className="org-user-list-wrapper">
                            <div className="org-user-list-inner">
                                {!newOrg ? currentPlayers.map((player: IPlayerItem, i: number) => {
                                    return (
                                        <div className={`org-user-list-item`} key={player.email}>
                                            <div className="org-user-list-item-title">{player.email}</div>
                                            <div className="checkbox-wrapper-14 org-checkbox">
                                                <input className="tgl tgl-ios" id={`cb2-1${i+5}`} value={player.selected.toString()} checked={player.selected} onChange={togglePlayer} data-email={player.email} data-orgid={selectedOption!.value} data-orgname={player.orgName} type="checkbox" disabled={player.selected} />
                                                <label className="tgl-btn" htmlFor={`cb2-1${i+5}`}></label>
                                            </div>
                                        </div>
                                    )
                                }) : <></>}
                            </div>
                        </div>
                    </div>
                </div>

                <button onClick={closeManageOrgsModal} className="new-confirm-btn one-modal-btn"><span>Done</span></button>
            </Modal>
        </div>
    )
}

export default GameLobby;